/**
 * The pre-call gate — does this customer still owe a verification step before the video leg?
 *
 * WHY THIS EXISTS. The invite link lands on `/session/:id`, whose CTA sent the customer to `/call/:id`
 * whenever the session was not in a `preparing` state. `/call` is the one entry route that never fetched the
 * frozen check plan, so the moment an officer warmed a session to READY the customer's entire pre-call rail
 * — DigiLocker Aadhaar included — silently disappeared. Measured on live data: every completed session that
 * held a frozen plan completed with none of its REQUIRED checks satisfied.
 *
 * The routing decision is therefore made HERE, from the plan, as a pure function — not inferred from the
 * session's FSM state. FSM state answers "may the customer join?"; it cannot answer "has the customer done
 * what the regime requires first?". Only the plan answers that.
 *
 * NOTE ON `CUSTOMER_FACING_PRECALL_CHECKS`: which checks a customer acts on is currently a client-side set
 * (see checkPlan.ts), because the wire carries no `actor` field. That is a known INV-13 wrinkle — a hardcoded
 * client set deciding which regulated checks a customer sees. It is retained here deliberately for this
 * change (droom-only, no backend dependency) and is scheduled for deletion the moment the plan rows carry a
 * server-derived `actor`. This module is where that swap lands: change `owedPreCallChecks` and nothing else.
 */
import type { CheckStatusDTO, PlanCheckDTO, SessionChecksDTO } from '@/types';
import { CUSTOMER_FACING_PRECALL_CHECKS } from './checkPlan';

/** Statuses that discharge a REQUIRED check. Anything else (incl. RUNNING) still owes. */
const SATISFYING: ReadonlySet<string> = new Set([
  'PASSED',
  'VERIFIED',
  'FALLBACK_VERIFIED',
  'WAIVED',
  'NOT_APPLICABLE',
]);

export function isSatisfied(status: CheckStatusDTO | undefined): boolean {
  return status !== undefined && SATISFYING.has(status.status);
}

/**
 * The customer-actionable PRE_CALL checks that are REQUIRED and not yet satisfied.
 * Empty ⇒ the customer may proceed to the call.
 */
export function owedPreCallChecks(dto: SessionChecksDTO | null): readonly PlanCheckDTO[] {
  if (!dto) return [];
  const byKey = new Map(dto.statuses.map((s) => [s.checkKey, s]));
  return dto.plan
    .filter(
      (p) =>
        p.phase === 'PRE_CALL' &&
        p.requirement === 'REQUIRED' &&
        CUSTOMER_FACING_PRECALL_CHECKS.has(p.checkKey) &&
        !isSatisfied(byKey.get(p.checkKey)),
    )
    .slice()
    .sort((a, b) => a.order - b.order);
}

/**
 * What the entry surfaces should do.
 *
 * `blocked` is the fail-closed outcome and it is NOT an error state to be swallowed: the plan is never
 * legitimately empty (the regime floor guarantees rows), so an unreadable or empty plan means we do not know
 * what this customer owes. Substituting a hardcoded flow there would mean silently running a different
 * verification from the one the tenant configured and the floor validated.
 */
export type EntryDecision =
  | { kind: 'proceed' }
  | { kind: 'owes'; checks: readonly PlanCheckDTO[] }
  | { kind: 'blocked'; reason: 'unreadable' | 'empty' };

export function decideEntry(
  dto: SessionChecksDTO | null,
  fetchFailed: boolean,
): EntryDecision {
  if (fetchFailed) return { kind: 'blocked', reason: 'unreadable' };
  if (!dto || dto.plan.length === 0) return { kind: 'blocked', reason: 'empty' };
  const owed = owedPreCallChecks(dto);
  return owed.length > 0 ? { kind: 'owes', checks: owed } : { kind: 'proceed' };
}

/** May this session enter the call surface directly? Only an explicit `proceed` qualifies. */
export function mayEnterCall(decision: EntryDecision): boolean {
  return decision.kind === 'proceed';
}

/* ── The landing CTA (SessionLanding) — pure, so the dead-button class stays testable ─────────── */

export interface LandingCta {
  readonly labelKey: 'droom.landing.cta.join' | 'droom.landing.cta.start';
  readonly target: 'call' | 'start';
  /** The ONLY disabled state is 'checking' (first status read in flight). A customer who owes the
   *  pre-call rail STARTS it from every readable-or-unreadable state — 'joinable' included (a warmed
   *  READY session is exactly when the customer arrives) and 'unknown' included (/start is
   *  fail-closed, the honest route when the status read is denied). The 2026-08-01 preview P0 was
   *  this button rendering DISABLED on every warmed session: joinable + owesPreCall fell through
   *  `disabled={!canJoin && state !== 'preparing'}` and no customer could begin at all. */
  readonly enabled: boolean;
}

export function landingCta(
  state: 'checking' | 'preparing' | 'joinable' | 'unknown',
  owesPreCall: boolean,
): LandingCta {
  const canJoin = (state === 'joinable' || state === 'unknown') && !owesPreCall;
  return {
    labelKey: canJoin ? 'droom.landing.cta.join' : 'droom.landing.cta.start',
    target: canJoin ? 'call' : 'start',
    enabled: state !== 'checking',
  };
}
